import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Clock, CheckCircle, XCircle, X, RefreshCw, MessageSquare, ChevronDown, ArrowUpDown, Download, ExternalLink, Sparkles } from 'lucide-react';
import { database } from '../services/firebase';
import { ref, get } from 'firebase/database';
import { formatPrice, calculateRemainingDays, resolveScriptDownloadItems, formatDurationLabel } from '../utils/helpers';
import { getCurrentISTTime, displayTimeFromDB } from '../utils/timeUtils';
import { fetchProducts, selectAllProducts } from '../store/slices/productsSlice';
import { useTranslation } from 'react-i18next';

import Button from '../components/common/Button';
import ExtensionModal from '../components/modals/ExtensionModal';
import { getAuthRedirectPath } from '../utils/authRedirect';
import { isExtensionDiscountEligibleAccess } from '../utils/extensionEligibility';

const heroShellClass = 'relative overflow-hidden rounded-2xl border border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 text-gray-950 shadow-sm dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white dark:shadow-xl';
const heroGlowClass = 'absolute inset-0 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-amber-400/15 dark:from-primary-500/30 dark:via-accent-500/25 dark:to-amber-400/10';
const heroChipClass = 'mb-1.5 inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white/80 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-primary-100 dark:shadow-none';
const heroStatCardClass = 'min-w-12 rounded-xl border border-primary-100 bg-white/70 px-2 py-1.5 text-center shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:shadow-none';
const heroControlPanelClass = 'mt-3 grid grid-cols-[1fr_auto_auto] gap-2 rounded-xl border border-primary-100 bg-white/70 p-2 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:shadow-none';
const heroSelectClass = 'h-9 min-w-0 rounded-lg border border-primary-100 bg-white px-2 text-sm font-bold text-gray-900 outline-none focus:ring-2 focus:ring-amber-300 dark:border-white/10';
const heroButtonClass = 'inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-2.5 text-xs font-black text-white transition hover:bg-primary-700 dark:bg-white/10 dark:hover:bg-white/20';
const modalHeaderClass = 'relative border-b border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 px-4 py-4 text-gray-950 dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white sm:px-5';

const Orders = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { settings } = useSelector((state) => state.settings);
  const allProducts = useSelector(selectAllProducts);
  const productsLoaded = Array.isArray(allProducts) && allProducts.length > 0;
  const productIndex = useMemo(() => {
    const map = {};
    (allProducts || []).forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [allProducts]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [extensionModal, setExtensionModal] = useState(null);
  const [productAccessMap, setProductAccessMap] = useState({});
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [currentTime, setCurrentTime] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');

  const downloadCatalog = useMemo(() => resolveScriptDownloadItems(settings || {}), [settings]);

  const getOrderItemDownloads = useCallback((product) => {
    if (!product) return [];

    const hasLua = Boolean(product?.scriptFormats?.lua);
    const hasJson = Boolean(product?.scriptFormats?.json);

    if (!hasLua && !hasJson) return [];
    const luaItems = hasLua ? downloadCatalog.luaItems : [];
    const jsonItems = hasJson ? downloadCatalog.jsonItems : [];

    const merged = [];
    const seen = new Set();

    const addItems = (items = [], scriptType = '') => {
      items.forEach((item, index) => {
        if (!item?.link) return;
        const key = `${item.link}|${scriptType || item.type || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push({
          id: `${scriptType || item.type || 'file'}-${index}-${item.link}`,
          name: item.name || 'Download File',
          description: item.description || '',
          link: item.link,
          scriptType: scriptType || item.type || '',
        });
      });
    };

    addItems(luaItems, 'lua');
    addItems(jsonItems, 'json');
    return merged;
  }, [downloadCatalog]);

  useEffect(() => {
    const fetchCurrentTime = async () => {
      try {
        const time = await getCurrentISTTime();
        const parts = time.split(' ');
        let dateObject;
        if (parts.length === 2) {
          const dateParts = parts[0].split(':');
          const timeParts = parts[1].split(':');
          if (dateParts.length === 3 && timeParts.length === 3) {
            dateObject = new Date(dateParts[2], dateParts[1] - 1, dateParts[0], timeParts[0], timeParts[1], timeParts[2]);
          }
        }
        if (!dateObject || isNaN(dateObject)) {
          dateObject = new Date();
        }
        setCurrentTime(dateObject);
      } catch (error) {
        setCurrentTime(new Date());
      }
    };

    fetchCurrentTime();
    const intervalId = setInterval(fetchCurrentTime, 30000);
    return () => clearInterval(intervalId);
  }, []);



  useEffect(() => {
    if (!productsLoaded) {
      dispatch(fetchProducts());
    }
  }, [dispatch, productsLoaded]);

  const loadOrders = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const userOrdersSnapshot = await get(ref(database, `Users/${user.uid}/Orders`));
      const productAccessSnapshot = await get(ref(database, `Users/${user.uid}/ProductAccess`));
      setProductAccessMap(productAccessSnapshot.exists() ? (productAccessSnapshot.val() || {}) : {});
      const orderMap = new Map();
      const legacyIds = [];
      const userOrders = userOrdersSnapshot.exists() ? userOrdersSnapshot.val() || {} : {};
      
      Object.entries(userOrders).forEach(([orderId, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          orderMap.set(orderId, value);
        } else {
          legacyIds.push(orderId);
        }
      });

      if (legacyIds.length > 0) {
        // Resolve pointers using Promise.all for high performance parallel fetching
        const fetchPromises = legacyIds.map(async (id) => {
          try {
            const snap = await get(ref(database, `Order index/${id}`));
            if (snap.exists()) return { id, data: snap.val() };
          } catch (e) {
            console.warn(`Failed to fetch order ${id}:`, e);
          }
          return null;
        });
        
        const results = await Promise.all(fetchPromises);
        results.forEach(result => {
          if (result && result.data && typeof result.data === 'object' && result.data.Customer === user.uid) {
             orderMap.set(result.id, result.data);
          }
        });
      }

      const toNumber = (value, fallback = 0) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const parseDateTime = (value) => {
        if (value == null || value === '') return 0;

        if (typeof value === 'number' && Number.isFinite(value)) {
          if (value > 1e11) return value; // milliseconds
          if (value > 1e9) return value * 1000; // seconds
          return 0;
        }

        if (value instanceof Date) {
          const ts = value.getTime();
          return Number.isFinite(ts) ? ts : 0;
        }

        const cleaned = String(value).trim();
        if (!cleaned) return 0;

        if (/^\d+$/.test(cleaned)) {
          const numeric = Number(cleaned);
          if (Number.isFinite(numeric)) {
            if (numeric > 1e11) return numeric; // milliseconds
            if (numeric > 1e9) return numeric * 1000; // seconds
          }
        }

        const nativeParsed = Date.parse(cleaned);
        if (Number.isFinite(nativeParsed)) {
          return nativeParsed;
        }

        // dd:mm:yyyy / dd-mm-yyyy / dd/mm/yyyy with optional time
        const [datePart, timePart = '00:00:00'] = cleaned.split(/[\sT]+/);
        const dParts = datePart.split(/[.:\-/]/).map((part) => parseInt(part, 10));
        const tParts = timePart.split(':').map((part) => parseInt(part, 10));

        if (dParts.length === 3 && dParts.every((n) => Number.isFinite(n))) {
          let day;
          let month;
          let year;

          // Supports yyyy-mm-dd and dd-mm-yyyy
          if (dParts[0] > 31) {
            year = dParts[0];
            month = dParts[1];
            day = dParts[2];
          } else {
            day = dParts[0];
            month = dParts[1];
            year = dParts[2];
          }

          const hours = Number.isFinite(tParts[0]) ? tParts[0] : 0;
          const minutes = Number.isFinite(tParts[1]) ? tParts[1] : 0;
          const seconds = Number.isFinite(tParts[2]) ? tParts[2] : 0;

          const parsed = new Date(year, (month || 1) - 1, day || 1, hours, minutes, seconds);
          const timestamp = parsed.getTime();
          return Number.isFinite(timestamp) ? timestamp : 0;
        }

        return 0;
      };

      const normalized = Array.from(orderMap.entries())
        .map(([orderId, node]) => {
          if (!node || typeof node !== 'object') return null;
          if (node.Customer && node.Customer !== user.uid) return null;

          const items = Array.isArray(node.Items)
            ? node.Items.filter(Boolean).map((item) => {
              const itemType = item.itemType || (item.addonId || item.addonFor ? 'addon' : item.extensionFor || item.ExtensionFor ? 'extension' : 'product');
              return {
                productId: item.productId || item.ProductId || item.id,
                productName: item.productName || item.ProductName || item.name || null,
                quantity: toNumber(item.quantity ?? item.Quantity, 1) || 1,
                validity: item.validity ?? item.Validity ?? null,
                price: toNumber(item.price ?? item.Price),
                itemType,
                cartItemId: item.cartItemId || null,
                addonFor: item.addonFor || null,
                addonId: item.addonId || null,
                addonName: item.addonName || null,
                baseProductName: item.baseProductName || null,
                addonPricingSource: item.addonPricingSource || null,
                addonAdjustedDays: item.addonAdjustedDays || null,
                addonBaseValidity: item.addonBaseValidity || null,
                extensionFor: item.extensionFor || item.ExtensionFor || null,
                renewal: Boolean(item.renewal),
                remainingDays: item.remainingDays ?? null,
                upgradeFromValidity: item.upgradeFromValidity || null,
                upgradeToValidity: item.upgradeToValidity || null,
                upgradeDays: item.upgradeDays || null,
                refunded: Boolean(item.refunded),
                refundedAt: item.refundedAt || null,
                refundReason: item.refundReason || null,
                exchangedFrom: item.exchangedFrom || null,
                exchangedAt: item.exchangedAt || null,
                elapsedDaysDeducted: item.elapsedDaysDeducted ?? null,
              };
            })
            : [];

          const paymentInfo = node.Payments || {};
          const couponInfo = node.Coupon || null;
          const summary = node.Summary || {};
          const approvalDate = node.ApprovedAt || node['Order date'] || '';
          const rejectionReason = node.RejectionReason || '';
          const refundReason = node.RefundReason || '';

          const rawDate =
            node['Order date'] ??
            node.orderDate ??
            node.CreatedAt ??
            node.createdAt ??
            node.Timestamp ??
            node.timestamp ??
            node.Payments?.['Transaction date'] ??
            '';
          const timestamp = parseDateTime(rawDate);

          const subtotal =
            toNumber(summary.subtotal) > 0
              ? toNumber(summary.subtotal)
              : items.reduce(
                (total, item) => total + item.price * item.quantity,
                0
              );

          const shipping = toNumber(
            summary.shipping ?? paymentInfo.Shipping ?? node.Shipping
          );

          const couponDiscount = toNumber(
            paymentInfo['Coupon discount'] ??
            summary.promoDiscount ??
            couponInfo?.discount
          );

          const pointsDiscount = toNumber(
            node.pointsDiscount ?? node['points discount'] ?? summary.pointsDiscount ?? paymentInfo['Points discount']
          );

          const paymentAmount = toNumber(
            paymentInfo.Amount ?? summary.total,
            Math.max(
              0,
              subtotal + shipping - couponDiscount - pointsDiscount
            )
          );

          return {
            id: orderId,
            status: (node.Status || 'pending').toLowerCase(),
            date: rawDate,
            timestamp,
            approvalDate,
            rejectionReason,
            refundReason,
            items,
            itemsCount: items.length,
            subtotal,
            shipping,
            total: paymentAmount,
            partialRefunds: Array.isArray(node.PartialRefunds) ? node.PartialRefunds : (Array.isArray(node.partialRefunds) ? node.partialRefunds : []),
            exchanges: Array.isArray(node.Exchanges) ? node.Exchanges : (Array.isArray(node.exchanges) ? node.exchanges : []),
            payment: {
              method: paymentInfo['Pay method'] || paymentInfo.method || 'N/A',
              amount: paymentAmount,
              currency: paymentInfo['Currency'] || 'USD',
              convertedAmount: paymentInfo['Converted amount'] || null,
              exchangeRate: paymentInfo['Exchange rate'] || null,
              rateSource: paymentInfo['Rate source'] || null,
              transactionId:
                paymentInfo['Transaction id'] ||
                paymentInfo['Gift card number'] ||
                'N/A',
              transactionDate: paymentInfo['Transaction date'] || '',
              couponCode: paymentInfo['Coupon code'] || couponInfo?.code || null,
              couponDiscount,
              pointsDiscount,
              maxDiscount: couponInfo?.maxDiscount || null,
            },
          };
        })
        .filter(Boolean);

      setOrders(normalized);
    } catch (error) {
      setProductAccessMap({});
      console.error('Failed to load orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleRefresh = () => {
    loadOrders();
  };

  const filteredAndSortedOrders = useMemo(() => {
    let filtered = orders;

    // Apply filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'extension') {
        filtered = orders.filter(order => order.id.startsWith('EXT-'));
      } else {
        filtered = orders.filter(order => order.status === filterStatus && !order.id.startsWith('EXT-'));
      }
    }

    // Apply sorting
    return [...filtered].sort((a, b) => { // Use spread syntax to create a copy before sorting
      if (sortOrder === 'newest') {
        const diff = b.timestamp - a.timestamp;
        return diff !== 0 ? diff : String(b.id).localeCompare(String(a.id));
      } else {
        const diff = a.timestamp - b.timestamp;
        return diff !== 0 ? diff : String(a.id).localeCompare(String(b.id));
      }
    });
  }, [orders, filterStatus, sortOrder]);



  const handleOrderClick = (order) => setSelectedOrder(order);
  const closeOrderDetails = () => setSelectedOrder(null);


  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'cancelled':
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'refunded':
        return <RefreshCw className="w-5 h-5 text-purple-500" />;
      case 'partially_refunded':
        return <RefreshCw className="w-5 h-5 text-indigo-500" />;
      case 'exchanged':
        return <RefreshCw className="w-5 h-5 text-teal-500" />;
      default:
        return <Package className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusLabel = (statusKey) => {
    if (statusKey === 'pending') return t('orders.status_processing');
    if (statusKey === 'partially_refunded') return t('orders.partially_refunded', 'Partially Refunded');
    if (statusKey === 'exchanged') return t('orders.exchanged', 'Exchanged');
    return t(`orders.${statusKey}`, statusKey);
  };

  const formatOrderDate = (raw) => {
    return displayTimeFromDB(raw, user?.country, user?.language, user?.timezone);
  };

  const approvedCount = orders.filter((order) => ['approved', 'delivered'].includes(order.status)).length;
  const pendingCount = orders.filter((order) => order.status === 'pending').length;
  const extensionCount = orders.filter((order) => order.id.startsWith('EXT-')).length;



  if (!isAuthenticated) {
    return <Navigate to={getAuthRedirectPath()} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
      <div className="container mx-auto max-w-5xl px-3 sm:px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto space-y-4"
        >
          <section className={heroShellClass}>
            <div className={heroGlowClass} />
            <div className="relative p-3 sm:p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className={heroChipClass}>
                    <Sparkles className="h-3 w-3 text-amber-300" />
                    Purchase History
                  </div>
                  <h1 className="text-xl font-black tracking-normal sm:text-2xl">{t('orders.title')}</h1>
                  <p className="mt-0.5 hidden max-w-2xl text-xs font-medium text-gray-600 dark:text-gray-300 sm:block">
                    Track approvals, extensions, downloads, and payment details in one place.
                  </p>
                </div>

                <div className="grid shrink-0 grid-cols-3 gap-1.5">
                  {[
                    { label: 'All', value: orders.length },
                    { label: t('orders.approved'), value: approvedCount },
                    { label: t('orders.pending'), value: pendingCount },
                  ].map((stat) => (
                    <div key={stat.label} className={heroStatCardClass}>
                      <p className="truncate text-[9px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">{stat.label}</p>
                      <p className="text-sm font-black text-gray-950 dark:text-white notranslate" translate="no">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={heroControlPanelClass}>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className={heroSelectClass}
                >
                  <option value="all">{t('orders.all')}</option>
                  <option value="approved">{t('orders.approved')}</option>
                  <option value="pending">{t('orders.pending')}</option>
                  <option value="rejected">{t('orders.rejected')}</option>
                  <option value="refunded">{t('orders.refunded')}</option>
                  <option value="partially_refunded">{t('orders.partially_refunded', 'Partially Refunded')}</option>
                  <option value="exchanged">{t('orders.exchanged', 'Exchanged')}</option>
                  <option value="extension">{t('orders.extensions')} ({extensionCount})</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                  className={heroButtonClass}
                >
                  <ArrowUpDown className="h-4 w-4" />
                  {sortOrder === 'newest' ? t('orders.newest') : t('orders.oldest')}
                </button>
                <button
                  onClick={handleRefresh}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white text-gray-950 transition hover:bg-gray-100"
                  aria-label={t('orders.refresh')}
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </div>
          </section>



          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex animate-pulse items-center gap-3 rounded-3xl border border-gray-100 bg-white p-3 dark:border-gray-800 dark:bg-gray-900 sm:p-4">
                  <div className="h-10 w-10 shrink-0 rounded-2xl bg-gray-200 dark:bg-gray-700" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                  </div>
                  <div className="text-right space-y-2 shrink-0">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-16" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredAndSortedOrders.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <Package className="mx-auto mb-3 h-8 w-8 text-gray-400" />
              <p className="text-sm font-black text-gray-900 dark:text-white">
                {orders.length === 0 ? t('orders.no_found') : t('orders.no_match')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid gap-3">
                {filteredAndSortedOrders.map((order) => {
                  const statusKey = order.status;
                  const isExtensionOrder = order.id.startsWith('EXT-');
                  const statusIcon = getStatusIcon(statusKey);
                  const statusLabel = getStatusLabel(statusKey);

                  return (
                    <div
                      key={order.id}
                      className={`group relative cursor-pointer overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 ${isExtensionOrder ? 'ring-1 ring-blue-500/20' : ''
                        }`}
                      onClick={() => handleOrderClick(order)}
                    >
                      <div className="relative flex items-center gap-3 p-3 sm:p-4">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl shadow-inner ${statusKey === 'approved' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
                          statusKey === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' :
                            statusKey === 'cancelled' || statusKey === 'rejected' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                              statusKey === 'partially_refunded' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' :
                                statusKey === 'exchanged' ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400' :
                                  'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                          }`}>
                          {statusIcon}
                        </div>

                        <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
                          <div className="flex items-center gap-2">
                            <h3 className="truncate text-sm font-black tracking-tight text-gray-900 dark:text-white sm:text-base">{order.id}</h3>
                            {isExtensionOrder && (
                              <span className="shrink-0 rounded-full border border-blue-200 bg-blue-100 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-blue-600 dark:border-blue-800/50 dark:bg-blue-900/30 dark:text-blue-300">Ext</span>
                            )}
                          </div>
                          <div className="flex flex-col gap-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 sm:flex-row sm:items-center sm:gap-2">
                            <span className="truncate">{formatOrderDate(order.date)}</span>
                            <span className="hidden h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700 sm:block" />
                            <span className="whitespace-nowrap">{order.itemsCount} {order.itemsCount === 1 ? t('orders.item') : t('orders.items')}</span>
                          </div>
                        </div>

                        <div className="flex shrink-0 flex-col items-end gap-1 text-right sm:gap-1.5">
                          <p className="text-base font-black leading-none text-gray-900 dark:text-white sm:text-lg notranslate" translate="no">{formatPrice(order.total)}</p>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-wide sm:px-2.5 sm:py-1 sm:text-[11px] ${['approved', 'delivered'].includes(statusKey) ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' :
                            statusKey === 'pending' ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800' :
                              ['cancelled', 'rejected'].includes(statusKey) ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800' :
                                statusKey === 'refunded' ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800' :
                                statusKey === 'partially_refunded' ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800' :
                                  statusKey === 'exchanged' ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 border-teal-200 dark:border-teal-800' :
                                    'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                            }`}>
                            {statusLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedOrder && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[90] bg-black/[0.65] backdrop-blur-sm"
              onClick={closeOrderDetails}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-[91] flex items-center justify-center p-4"
              onClick={closeOrderDetails}
            >
              <div
                className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-gray-900"
                onClick={(e) => e.stopPropagation()}
              >
                <div className={modalHeaderClass}>
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-transparent dark:from-primary-500/20 dark:via-accent-500/[0.15]" />
                  <div className="relative flex items-center justify-between gap-4">
                    {/* Left Side: Icon + Details */}
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-primary-100 bg-white/80 dark:border-white/10 dark:bg-white/10">
                        <Package className="h-5 w-5 text-primary-600 dark:text-primary-200" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-base font-black text-gray-950 dark:text-white sm:text-lg" title={selectedOrder.id}>
                          {selectedOrder.id}
                        </h3>
                        <div className="mt-0.5 flex">
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${
                            selectedOrder.status === 'approved' ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-300/40 dark:bg-green-400/20 dark:text-green-200' :
                            selectedOrder.status === 'pending' ? 'border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-300/40 dark:bg-yellow-400/20 dark:text-yellow-100' :
                            selectedOrder.status === 'partially_refunded' ? 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-300/40 dark:bg-indigo-400/20 dark:text-indigo-200' :
                            selectedOrder.status === 'exchanged' ? 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-300/40 dark:bg-teal-400/20 dark:text-teal-200' :
                            selectedOrder.status === 'refunded' ? 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-300/40 dark:bg-purple-400/20 dark:text-purple-200' :
                            'border-gray-200 bg-white/80 text-gray-600 dark:border-white/20 dark:bg-white/10 dark:text-gray-200'} notranslate`}>
                            {getStatusLabel(selectedOrder.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right Side: Close Button */}
                    <button
                      className="-mr-1 rounded-full bg-white/80 p-2 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/20 dark:hover:text-white"
                      onClick={closeOrderDetails}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="max-h-[80vh] space-y-5 overflow-y-auto px-4 py-4 sm:px-5">
                  {/* Rejection/Refund Alerts */}
                  {(selectedOrder.status === 'rejected' || selectedOrder.status === 'refunded') && (selectedOrder.rejectionReason || selectedOrder.refundReason) && (
                    <div className="flex gap-3 rounded-2xl border border-red-100 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                      <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-bold text-red-800 dark:text-red-200 uppercase tracking-wide">
                          {selectedOrder.status === 'rejected' ? t('orders.rejection_reason') : t('orders.refund_reason')}
                        </h4>
                        <p className="text-red-600 dark:text-red-300 mt-1">
                          {selectedOrder.rejectionReason || selectedOrder.refundReason}
                        </p>
                      </div>
                    </div>
                  )}



                  {/* Items List */}
                  <div>
                    <h4 className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-gray-900 dark:text-white">
                      <div className="w-1 h-4 bg-primary-500 rounded-full" />
                      {t('orders.items_validity')}
                    </h4>
                    <div className="space-y-2">
                      {selectedOrder.items.length > 0 ? (
                        selectedOrder.items.map((item, index) => {
                          const product = productIndex[item.productId];
                          const isAddonItem = item.itemType === 'addon' || Boolean(item.addonId || item.addonFor);
                          const name = isAddonItem
                            ? item.addonName || item.productName || 'Addon Feature'
                            : product?.name || product?.Name || item.productName || item.productId;
                          const liveAccess = productAccessMap?.[item.productId] || null;
                          const liveAccessExpiry = liveAccess?.expiresAt ? new Date(liveAccess.expiresAt) : null;
                          const liveRemainingDays = liveAccessExpiry && Number.isFinite(liveAccessExpiry.getTime()) && liveAccessExpiry.getTime() > Date.now()
                            ? Math.ceil((liveAccessExpiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                            : null;
                          const extensionValidity = liveAccess?.validity || item.validity;
                          const extensionRemainingDays = liveRemainingDays ?? null;
                          const remainingDays = selectedOrder.status === 'approved' && item.validity && currentTime
                            ? calculateRemainingDays(selectedOrder.approvalDate, item.validity, currentTime)
                            : null;
                          const isLiveAccessExpired = liveAccess
                            ? String(liveAccess.status || 'active').toLowerCase() === 'expired' || (liveAccessExpiry && liveAccessExpiry.getTime() <= Date.now())
                            : null;
                          const isExpiredItem = isLiveAccessExpired ?? (remainingDays !== null && remainingDays <= 0);

                          return (
                            <div key={`${item.cartItemId || item.productId}-${item.addonId || item.itemType || 'item'}-${item.validity || 'default'}-${index}`} className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950">
                              <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-gray-400 dark:bg-gray-900">
                                  <Package className="h-[18px] w-[18px]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center flex-wrap gap-1.5">
                                    <p className={`truncate text-sm font-black ${item.refunded ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`} title={name}>
                                      {name}
                                    </p>
                                    {item.refunded && (
                                      <span className="inline-flex items-center rounded-full bg-rose-100 dark:bg-rose-900/40 px-2 py-0.5 text-[10px] font-bold text-rose-700 dark:text-rose-300 uppercase">
                                        {t('orders.refunded_item', 'Refunded')}
                                      </span>
                                    )}
                                    {item.exchangedFrom && (
                                      <span className="inline-flex items-center rounded-full bg-teal-100 dark:bg-teal-900/40 px-2 py-0.5 text-[10px] font-bold text-teal-700 dark:text-teal-300">
                                        {t('orders.exchanged_from', 'Exchanged from')} {item.exchangedFrom}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {item.validity ? formatDurationLabel(item.validity) : t('orders.lifetime')}
                                    {isAddonItem && <span className="font-medium text-violet-500"> - {t('common.addons', 'Addons')}</span>}
                                    {item.extensionFor && <span className="font-medium text-blue-500"> - {t('orders.extension')}</span>}
                                  </p>
                                  {item.elapsedDaysDeducted > 0 && (
                                    <p className="text-[11px] font-semibold text-teal-600 dark:text-teal-400">
                                      ({item.elapsedDaysDeducted} {t('orders.days_deducted', 'days deducted from previous usage')})
                                    </p>
                                  )}
                                  {isAddonItem && (
                                    <p className="mt-0.5 truncate text-[11px] font-semibold text-gray-400 dark:text-gray-500">
                                      {t('cart.addon_for', 'Addon for')} {item.baseProductName || product?.name || product?.Name || item.addonFor || item.productId}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="ml-2 shrink-0 text-right">
                                <p className={`font-black ${item.refunded ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-white'} notranslate`} translate="no">
                                  {formatPrice(item.price)}
                                </p>
                                {selectedOrder.status === 'approved' && item.validity && !isAddonItem && !item.refunded && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setExtensionModal({
                                      productId: item.productId,
                                      basePrice: item.price,
                                      productName: name,
                                      originalOrderId: selectedOrder.id,
                                      mode: isExpiredItem ? 'renew' : 'extend',
                                      currentValidity: extensionValidity,
                                      originalPaidPrice: item.price,
                                      remainingDays: extensionRemainingDays ?? remainingDays,
                                      extensionDiscountEligible: isExtensionDiscountEligibleAccess({
                                        productId: item.productId,
                                        accessRecord: liveAccess,
                                        orders
                                      }),
                                      accessSource: liveAccess?.source || liveAccess?.lastAdjustment?.source || '',
                                      addons: Array.isArray(product?.addons) ? product.addons : [],
                                      addonAccessMap: liveAccess?.addons && typeof liveAccess.addons === 'object' ? liveAccess.addons : {}
                                    })}
                                    className="mt-2 rounded-xl text-xs"
                                  >
                                    {isExpiredItem ? t('orders.renew', 'Renew') : t('orders.extend', 'Extend')}
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-4 text-gray-500">No items found</div>
                      )}
                    </div>
                  </div>

                  {selectedOrder.status === 'approved' && (
                    <div>
                      <h4 className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-gray-900 dark:text-white">
                        <div className="w-1 h-4 bg-primary-500 rounded-full" />
                        Download Related Files
                      </h4>
                      <div className="space-y-2">
                        {selectedOrder.items.length > 0 ? (
                          selectedOrder.items.map((item, index) => {
                            if (item.itemType === 'addon' || item.addonId || item.addonFor) return null;
                            const product = productIndex[item.productId];
                            const productName = product?.name || product?.Name || item.productId;
                            const downloadItems = getOrderItemDownloads(product);
                            const hasLua = Boolean(product?.scriptFormats?.lua);
                            const hasJson = Boolean(product?.scriptFormats?.json);

                            if (downloadItems.length === 0) {
                              return null;
                            }

                            return (
                              <div key={`download-${item.productId}-${index}`} className="rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950">
                                <div className="flex items-center justify-between gap-3 mb-3">
                                  <p className="truncate text-sm font-black text-gray-900 dark:text-white" title={productName}>
                                    {productName}
                                  </p>
                                  <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {t(downloadItems.length > 1 ? 'orders.files_count' : 'orders.file_count', { count: downloadItems.length }, `${downloadItems.length} file${downloadItems.length > 1 ? 's' : ''}`)}
                                  </span>
                                </div>
                                <div className="mb-3 rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50/70 dark:bg-blue-900/20 p-3">
                                  {hasLua && (
                                    <p className="mt-1 text-[11px] text-gray-700 dark:text-gray-300">
                                      {t('downloads.gg_hint', 'Download GG files if you want to use Game Guardian.')}
                                    </p>
                                  )}
                                  {hasJson && (
                                    <p className="mt-1 text-[11px] text-gray-700 dark:text-gray-300">
                                      {t('downloads.mod_hint', 'Download Mod Menu files if you want to use RuntimeX / Js Hook.')}
                                    </p>
                                  )}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {downloadItems.map((downloadItem) => (
                                    <a
                                      key={downloadItem.id}
                                      href={downloadItem.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900/70 dark:hover:bg-gray-800"
                                    >
                                      <div className="min-w-0">
                                        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                          {downloadItem.name}
                                        </div>
                                        {downloadItem.scriptType && (
                                          <div className="text-[10px] uppercase tracking-wide text-primary-600 dark:text-primary-400 mt-0.5">
                                            {downloadItem.scriptType === 'lua' ? t('downloads.game_guardian_short', 'Game Guardian') : t('downloads.mod_menu_short', 'Mod Menu')}
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1 text-primary-600 dark:text-primary-400 shrink-0">
                                        <Download className="w-4 h-4" />
                                        <ExternalLink className="w-3.5 h-3.5" />
                                      </div>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            );
                          }).filter(Boolean)
                        ) : null}
                      </div>
                    </div>
                  )}

                  {/* Partial Refund History */}
                  {selectedOrder.partialRefunds && selectedOrder.partialRefunds.length > 0 && (
                    <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4 dark:border-indigo-900/40 dark:bg-indigo-950/20 space-y-2.5">
                      <h4 className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-indigo-900 dark:text-indigo-300">
                        <RefreshCw className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                        {t('orders.partial_refund_history', 'Partial Refund History')} ({selectedOrder.partialRefunds.length})
                      </h4>
                      <div className="divide-y divide-indigo-100 dark:divide-indigo-900/40 text-xs">
                        {selectedOrder.partialRefunds.map((refRecord, rIdx) => {
                          const refundItemNames = Array.isArray(refRecord.itemNames)
                            ? refRecord.itemNames.join(', ')
                            : (Array.isArray(refRecord.items) ? refRecord.items.map(it => it.name || it.id).join(', ') : t('orders.selected_items', 'Refunded Items'));
                          const isWallet = refRecord.destination === 'wallet';
                          return (
                            <div key={rIdx} className="py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                              <div>
                                <p className="font-black text-gray-900 dark:text-white">{refundItemNames}</p>
                                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                  {refRecord.refundedAt && <span>{formatOrderDate(refRecord.refundedAt)}</span>}
                                  <span>• Destination: <b className="text-gray-700 dark:text-gray-300">{isWallet ? 'Reward Points (Wallet)' : 'Payment Source'}</b></span>
                                  {refRecord.reason && <span>• Reason: {refRecord.reason}</span>}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <span className="font-black text-indigo-700 dark:text-indigo-300 text-sm">
                                  {isWallet && refRecord.refundPoints
                                    ? `+${refRecord.refundPoints} pts`
                                    : formatPrice(refRecord.amount ?? refRecord.refundAmount ?? 0)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Product Exchange History */}
                  {selectedOrder.exchanges && selectedOrder.exchanges.length > 0 && (
                    <div className="rounded-2xl border border-teal-100 bg-teal-50/70 p-4 dark:border-teal-900/40 dark:bg-teal-950/20 space-y-2.5">
                      <h4 className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-teal-900 dark:text-teal-300">
                        <RefreshCw className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                        {t('orders.exchange_history', 'Product Exchange History')} ({selectedOrder.exchanges.length})
                      </h4>
                      <div className="divide-y divide-teal-100 dark:divide-teal-900/40 text-xs">
                        {selectedOrder.exchanges.map((exch, eIdx) => {
                          const daysDeducted = exch.elapsedDays || exch.newItem?.elapsedDaysDeducted || 0;
                          return (
                            <div key={eIdx} className="py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                              <div>
                                <div className="font-black text-gray-900 dark:text-white flex items-center gap-1.5 flex-wrap">
                                  <span>{exch.oldItem?.name || 'Previous Product'}</span>
                                  <span className="text-teal-500 font-bold">➔</span>
                                  <span className="text-teal-700 dark:text-teal-300">
                                    {exch.newItem?.name || 'New Product'} ({formatDurationLabel(exch.newItem?.validity || exch.newItem?.baseValidity || 'lifetime')})
                                  </span>
                                </div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                  {exch.exchangedAt && <span>{formatOrderDate(exch.exchangedAt)}</span>}
                                  {daysDeducted > 0 && (
                                    <span className="font-semibold text-teal-600 dark:text-teal-400">
                                      • {daysDeducted} day{daysDeducted === 1 ? '' : 's'} deducted from prior usage
                                    </span>
                                  )}
                                  {exch.reason && <span>• Reason: {exch.reason}</span>}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                {exch.diffPoints ? (
                                  <span className={`font-black text-xs ${exch.diffPoints > 0 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                    {exch.diffPoints > 0 ? `+${exch.diffPoints} pts credited` : `${exch.diffPoints} pts deducted`}
                                  </span>
                                ) : (
                                  <span className="font-black text-xs text-teal-600 dark:text-teal-300">
                                    Diff: {exch.diffAmount > 0 ? `+${exch.diffAmount.toFixed(2)}` : `${(exch.diffAmount || 0).toFixed(2)}`}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Order Details Accordion */}
                  <div className="border border-gray-100 dark:border-gray-700/50 rounded-xl overflow-hidden mt-2">
                    <button
                      onClick={() => setShowOrderDetails(!showOrderDetails)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                        <div className="w-1 h-4 bg-primary-500 rounded-full" />
                        {t('orders.order_info')}
                      </h4>
                      <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${showOrderDetails ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {showOrderDetails && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="border-t border-gray-100 dark:border-gray-700/50">
                            {/* Info Grid */}
                            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700/50">
                                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">{t('orders.date_placed')}</span>
                                <span className="font-medium text-gray-900 dark:text-white">{formatOrderDate(selectedOrder.date)}</span>
                              </div>
                              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700/50">
                                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">{t('orders.payment_method')}</span>
                                <span className="font-medium text-gray-900 dark:text-white">{selectedOrder.payment.method}</span>
                              </div>
                              {selectedOrder.payment.convertedAmount && (
                                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700/50">
                                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">Converted Amount</span>
                                  <span className="font-mono text-sm font-semibold text-primary-600 dark:text-primary-400 block notranslate" translate="no">
                                    {selectedOrder.payment.convertedAmount} {selectedOrder.payment.currency || ""}
                                  </span>
                                  {selectedOrder.payment.exchangeRate && (
                                    <span className="text-[11px] text-gray-500 dark:text-gray-400 block mt-0.5 notranslate" translate="no">
                                      Rate: {selectedOrder.payment.currency === "LTC" ? `1 USDT ≈ ${selectedOrder.payment.exchangeRate} LTC` : selectedOrder.payment.exchangeRate}
                                    </span>
                                  )}
                                </div>
                              )}
                              {selectedOrder.payment.transactionId && selectedOrder.payment.transactionId !== 'N/A' && (
                                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700/50 col-span-1 sm:col-span-2">
                                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">{t('orders.transaction_ref')}</span>
                                  <span className="font-mono text-sm text-gray-700 dark:text-gray-300 break-all">{selectedOrder.payment.transactionId}</span>
                                </div>
                              )}
                              {selectedOrder.payment['Gift card number'] && (
                                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700/50 col-span-1 sm:col-span-2">
                                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">Gift Card Number</span>
                                  <span className="font-mono text-sm text-gray-700 dark:text-gray-300 break-all">{selectedOrder.payment['Gift card number']}</span>
                                </div>
                              )}
                            </div>

                            {/* Payment Summary */}
                            <div className="p-4 bg-gray-50/50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-700/50">
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500 dark:text-gray-400">{t('orders.subtotal')}</span>
                                  <span className="font-medium text-gray-900 dark:text-white notranslate" translate="no">{formatPrice(selectedOrder.subtotal)}</span>
                                </div>
                                {selectedOrder.shipping > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">{t('orders.shipping')}</span>
                                    <span className="font-medium text-gray-900 dark:text-white notranslate" translate="no">{formatPrice(selectedOrder.shipping)}</span>
                                  </div>
                                )}
                                {selectedOrder.payment.pointsDiscount > 0 && (
                                  <div className="flex justify-between text-green-600 dark:text-green-400">
                                    <span>{t('orders.points_used')}</span>
                                    <span className="notranslate" translate="no">-{formatPrice(selectedOrder.payment.pointsDiscount)}</span>
                                  </div>
                                )}
                                {selectedOrder.payment.couponDiscount > 0 && (
                                  <div className="flex justify-between text-green-600 dark:text-green-400">
                                    <div className="flex items-center gap-1">
                                      <span>{t('orders.coupon')} {selectedOrder.payment.couponCode && `(${selectedOrder.payment.couponCode})`}</span>
                                      {selectedOrder.payment.maxDiscount && (
                                        <span className="text-xs opacity-75 notranslate" translate="no">
                                          (Max ${selectedOrder.payment.maxDiscount})
                                        </span>
                                      )}
                                    </div>
                                    <span className="notranslate" translate="no">-{formatPrice(selectedOrder.payment.couponDiscount)}</span>
                                  </div>
                                )}
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                                  <div className="flex justify-between text-base font-bold text-gray-900 dark:text-white">
                                    <span>{t('orders.total_paid')}</span>
                                    <span className="notranslate" translate="no">{formatPrice(selectedOrder.total)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Footer Actions */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <a href="https://t.me/pshsupport" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl font-bold text-sm hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                      <MessageSquare className="w-4 h-4" />
                      {t('orders.contact_support')}
                    </a>
                    <Button variant="primary" onClick={closeOrderDetails} className="w-full h-full">
                      {t('orders.close')}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <ExtensionModal
        isOpen={!!extensionModal}
        onClose={() => setExtensionModal(null)}
        data={extensionModal}
      />
    </div>
  );
};

export default Orders;

